<template>
  <div class="convert-page">
    <h2 class="page-title">时区转换器</h2>
    <p class="page-desc">选择一个时区，输入时间，转换到其他时区</p>

    <div class="convert-form">
      <div class="form-row">
        <label>源时区 / 城市</label>
        <select v-model="sourceTz" class="tz-select">
          <optgroup v-for="group in tzGroups" :key="group.label" :label="group.label">
            <option v-for="tz in group.items" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
          </optgroup>
        </select>
      </div>

      <div class="form-row form-row-split">
        <div class="form-col">
          <label>日期</label>
          <input type="date" v-model="inputDate" class="date-input" />
        </div>
        <div class="form-col">
          <label>时间</label>
          <input type="time" v-model="inputTime" step="1" class="time-input" />
        </div>
        <div class="form-col">
          <label>&nbsp;</label>
          <button class="now-btn" @click="setNow">现在</button>
        </div>
      </div>

      <div class="form-row">
        <label>目标时区（可多选）</label>
        <div class="target-tags">
          <span
            v-for="tz in quickTargets"
            :key="tz.value"
            class="tz-tag"
            :class="{ selected: selectedTargets.includes(tz.value) }"
            @click="toggleTarget(tz.value)"
          >{{ tz.label }}</span>
        </div>
        <select v-model="customTz" class="tz-select add-select" @change="addCustomTz">
          <option value="">+ 添加其他时区</option>
          <optgroup v-for="group in tzGroups" :key="'t-'+group.label" :label="group.label">
            <option v-for="tz in group.items" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
          </optgroup>
        </select>
      </div>
    </div>

    <!-- 转换结果 -->
    <div class="convert-results" v-if="selectedTargets.length > 0">
      <h3 class="result-title">
        转换结果：<span class="src-info">{{ sourceTzLabel }} {{ inputDate }} {{ inputTime }}</span>
      </h3>
      <div class="result-cards">
        <div
          v-for="r in results"
          :key="r.value"
          class="result-card"
        >
          <div class="rc-name">{{ r.label }}</div>
          <div class="rc-time">{{ r.resultTime }}</div>
          <div class="rc-date">{{ r.resultDate }} {{ r.weekday }}</div>
          <div class="rc-offset">{{ r.offsetStr }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const TZ_GROUPS = [
  { label: '亚洲', items: [
    { label: '北京 (UTC+8)', value: 'Asia/Shanghai' },
    { label: '东京 (UTC+9)', value: 'Asia/Tokyo' },
    { label: '首尔 (UTC+9)', value: 'Asia/Seoul' },
    { label: '新加坡 (UTC+8)', value: 'Asia/Singapore' },
    { label: '香港 (UTC+8)', value: 'Asia/Hong_Kong' },
    { label: '迪拜 (UTC+4)', value: 'Asia/Dubai' },
    { label: '孟买 (UTC+5:30)', value: 'Asia/Kolkata' },
    { label: '曼谷 (UTC+7)', value: 'Asia/Bangkok' },
    { label: '雅加达 (UTC+7)', value: 'Asia/Jakarta' },
    { label: '台北 (UTC+8)', value: 'Asia/Taipei' },
    { label: '上海 (UTC+8)', value: 'Asia/Shanghai' }
  ]},
  { label: '欧洲', items: [
    { label: '伦敦 (UTC+0/+1)', value: 'Europe/London' },
    { label: '巴黎 (UTC+1/+2)', value: 'Europe/Paris' },
    { label: '柏林 (UTC+1/+2)', value: 'Europe/Berlin' },
    { label: '莫斯科 (UTC+3)', value: 'Europe/Moscow' },
    { label: '罗马 (UTC+1/+2)', value: 'Europe/Rome' },
    { label: '马德里 (UTC+1/+2)', value: 'Europe/Madrid' },
    { label: '阿姆斯特丹 (UTC+1/+2)', value: 'Europe/Amsterdam' },
    { label: '斯德哥尔摩 (UTC+1/+2)', value: 'Europe/Stockholm' },
    { label: '伊斯坦布尔 (UTC+3)', value: 'Europe/Istanbul' }
  ]},
  { label: '北美', items: [
    { label: '纽约 (UTC-5/-4)', value: 'America/New_York' },
    { label: '洛杉矶 (UTC-8/-7)', value: 'America/Los_Angeles' },
    { label: '芝加哥 (UTC-6/-5)', value: 'America/Chicago' },
    { label: '多伦多 (UTC-5/-4)', value: 'America/Toronto' },
    { label: '温哥华 (UTC-8/-7)', value: 'America/Vancouver' },
    { label: '墨西哥城 (UTC-6)', value: 'America/Mexico_City' }
  ]},
  { label: '南美', items: [
    { label: '圣保罗 (UTC-3)', value: 'America/Sao_Paulo' },
    { label: '布宜诺斯艾利斯 (UTC-3)', value: 'America/Argentina/Buenos_Aires' },
    { label: '利马 (UTC-5)', value: 'America/Lima' },
    { label: '圣地亚哥 (UTC-4/-3)', value: 'America/Santiago' }
  ]},
  { label: '大洋洲', items: [
    { label: '悉尼 (UTC+10/+11)', value: 'Australia/Sydney' },
    { label: '奥克兰 (UTC+12/+13)', value: 'Pacific/Auckland' }
  ]},
  { label: '非洲', items: [
    { label: '开罗 (UTC+2/+3)', value: 'Africa/Cairo' },
    { label: '拉各斯 (UTC+1)', value: 'Africa/Lagos' },
    { label: '约翰内斯堡 (UTC+2)', value: 'Africa/Johannesburg' },
    { label: '内罗毕 (UTC+3)', value: 'Africa/Nairobi' }
  ]}
]

const QUICK_TARGETS = [
  { label: '北京+8', value: 'Asia/Shanghai' },
  { label: '东京+9', value: 'Asia/Tokyo' },
  { label: '伦敦+0', value: 'Europe/London' },
  { label: '纽约-5', value: 'America/New_York' },
  { label: '洛杉矶-8', value: 'America/Los_Angeles' },
  { label: '巴黎+1', value: 'Europe/Paris' },
  { label: '莫斯科+3', value: 'Europe/Moscow' },
  { label: '迪拜+4', value: 'Asia/Dubai' },
  { label: '悉尼+10', value: 'Australia/Sydney' },
  { label: '孟买+5:30', value: 'Asia/Kolkata' }
]

export default {
  name: 'ConvertPage',
  data() {
    const now = new Date()
    return {
      tzGroups: TZ_GROUPS,
      quickTargets: QUICK_TARGETS,
      sourceTz: 'Asia/Shanghai',
      inputDate: now.toISOString().slice(0, 10),
      inputTime: now.toTimeString().slice(0, 8),
      selectedTargets: ['America/New_York', 'Europe/London', 'Asia/Tokyo'],
      customTz: '',
      results: []
    }
  },
  computed: {
    sourceTzLabel() {
      const all = TZ_GROUPS.flatMap(g => g.items)
      const found = all.find(t => t.value === this.sourceTz)
      return found ? found.label.split(' ')[0] : this.sourceTz
    }
  },
  watch: {
    sourceTz() { this.convert() },
    inputDate() { this.convert() },
    inputTime() { this.convert() },
    selectedTargets() { this.convert() }
  },
  mounted() { this.convert() },
  methods: {
    setNow() {
      const now = new Date()
      this.inputDate = now.toISOString().slice(0, 10)
      this.inputTime = now.toTimeString().slice(0, 8)
    },
    toggleTarget(val) {
      const idx = this.selectedTargets.indexOf(val)
      if (idx >= 0) this.selectedTargets.splice(idx, 1)
      else this.selectedTargets.push(val)
    },
    addCustomTz() {
      if (this.customTz && !this.selectedTargets.includes(this.customTz)) {
        this.selectedTargets.push(this.customTz)
      }
      this.customTz = ''
    },
    convert() {
      if (!this.sourceTz || !this.inputDate || !this.inputTime) return
      // Parse source time
      const srcDate = new Date(this.inputDate + 'T' + this.inputTime)
      if (isNaN(srcDate.getTime())) return

      // Get UTC timestamp
      const srcStr = srcDate.toLocaleString('en-US', { timeZone: this.sourceTz })
      const utcDate = new Date(srcStr)
      if (isNaN(utcDate.getTime())) return

      this.results = this.selectedTargets.map(tz => {
        const all = TZ_GROUPS.flatMap(g => g.items).concat(QUICK_TARGETS)
        const found = all.find(t => t.value === tz)
        const label = found ? found.label : tz
        const rDate = new Date(utcDate.toLocaleString('en-US', { timeZone: tz }))

        const time = rDate.toLocaleTimeString('zh-CN', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
        const date = rDate.toLocaleDateString('zh-CN', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
        const weekday = rDate.toLocaleDateString('zh-CN', { timeZone: tz, weekday: 'long' })

        let offsetStr = ''
        try {
          const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(utcDate)
          const o = parts.find(p => p.type === 'timeZoneName')
          if (o) offsetStr = o.value
        } catch (e) { /* ignore */ }

        return { value: tz, label, resultTime: time, resultDate: date, weekday, offsetStr }
      })
    }
  }
}
</script>

<style scoped>
.convert-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 16px 40px;
}
.page-title {
  font-size: 22px;
  color: #1a1a2e;
  margin-top: 24px;
  text-align: center;
}
.page-desc {
  text-align: center;
  font-size: 13px;
  color: #888;
  margin: 6px 0 20px;
}

.convert-form {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.form-row {
  margin-bottom: 18px;
}
.form-row label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 6px;
}
.form-row-split {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}
.form-col { flex: 1; }

.tz-select, .date-input, .time-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  background: #fff;
}
.tz-select:focus, .date-input:focus, .time-input:focus {
  outline: none;
  border-color: #1677ff;
  box-shadow: 0 0 0 3px rgba(22,119,255,0.1);
}
.now-btn {
  padding: 10px 20px;
  background: #1677ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: opacity 0.2s;
}
.now-btn:hover { opacity: 0.85; }

.target-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.tz-tag {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
}
.tz-tag:hover { border-color: #1677ff; color: #1677ff; }
.tz-tag.selected { background: #1677ff; color: #fff; border-color: #1677ff; }
.add-select { margin-top: 4px; }

/* Results */
.convert-results { margin-top: 24px; }
.result-title {
  font-size: 15px;
  color: #555;
  margin-bottom: 14px;
}
.src-info { color: #1677ff; font-weight: 600; }

.result-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.result-card {
  background: #fff;
  border-radius: 10px;
  padding: 18px 14px;
  text-align: center;
  box-shadow: 0 1px 8px rgba(0,0,0,0.05);
}
.rc-name { font-size: 14px; font-weight: 600; color: #333; margin-bottom: 6px; }
.rc-time {
  font-size: 28px;
  font-weight: 300;
  font-family: 'SF Mono', 'Consolas', 'Menlo', monospace;
  color: #1a1a2e;
}
.rc-date { font-size: 12px; color: #999; margin-top: 3px; }
.rc-offset { font-size: 11px; color: #1677ff; margin-top: 2px; font-weight: 600; }

@media (max-width: 768px) {
  .result-cards { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 480px) {
  .result-cards { grid-template-columns: 1fr; }
  .form-row-split { flex-direction: column; }
}
</style>
